import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { WorkPartsFields } from './WorkPartsFields';
import { serializedPayload } from '../lib/serializedWork';
import FollowUpFields, { emptyFollowUp } from './FollowUpFields';
import InstallationPointMultiSelect from './InstallationPointMultiSelect';

const QuickMaintenanceDialog = ({ open, onClose, onSuccess, resolvesFollowUp }) => {
  // Step 1: Select equipment, Step 2: Fill maintenance form
  const [step, setStep] = useState(1);
  
  // Selection state
  const [locations, setLocations] = useState([]);
  const [machinery, setMachinery] = useState([]);
  const [installationPoints, setInstallationPoints] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [selectedMachineryId, setSelectedMachineryId] = useState('');
  const [selectedInstallationPointIds, setSelectedInstallationPointIds] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [optionsError, setOptionsError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    work_description: '',
    resolution: '',
    hours_spent: '',
    work_date: '',
    follow_up_required: '',
    notes: ''
  });
  const [selectedParts, setSelectedParts] = useState([]);
  const [serializedParts, setSerializedParts] = useState([]);
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [followUp, setFollowUp] = useState(emptyFollowUp());
  const [imageFiles, setImageFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);

  // Fetch locations on open
  useEffect(() => {
    if (!open) return;

    // Regular "Add Maintenance Log" flow — start at step 1 (Location → Equipment).
    if (!resolvesFollowUp?.source_machinery_id) {
      setStep(1);
      setSelectedLocationId('');
      setSelectedMachineryId('');
      setSelectedInstallationPointIds([]);
      setMachinery([]);
      setInstallationPoints([]);
      resetForm();
      fetchLocations();
      return;
    }

    // "Resolve follow-up" flow — skip step 1 entirely. Pre-fetch the machinery
    // record so `selectedMachinery.component` renders in the header, then jump
    // to step 2 with the work_description pre-filled referencing the follow-up.
    (async () => {
      resetForm();
      try {
        const machRes = await axios.get(
          `${API}/machinery/${resolvesFollowUp.source_machinery_id}`
        );
        const m = machRes.data;
        setMachinery([m]);  // populate lookup array so selectedMachinery resolves
        setSelectedMachineryId(m.id);
        setSelectedInstallationPointIds([]);
        setFormData((prev) => ({
          ...prev,
          work_description: `Follow-up: ${resolvesFollowUp.description || ''}`.slice(0, 500),
        }));
        setStep(2);
      } catch (e) {
        // Fall back to step 1 if the fetch fails.
        setStep(1);
        fetchLocations();
        toast.error('Unable to pre-load equipment; please pick manually.');
      }
    })();
  }, [open]);

  // Fetch machinery when location changes
  useEffect(() => {
    if (selectedLocationId) {
      fetchMachinery(selectedLocationId);
      setSelectedMachineryId('');
      setSelectedInstallationPointIds([]);
      setInstallationPoints([]);
    } else if (!resolvesFollowUp) {
      // In "Resolve follow-up" mode the machinery array is pre-populated by
      // the open-effect above; leave it alone even though there's no location.
      setMachinery([]);
    }
  }, [selectedLocationId]);

  // Fetch installation points when machinery changes
  useEffect(() => {
    setSerializedParts([]);
    if (selectedMachineryId) {
      fetchInstallationPoints(selectedMachineryId);
      setSelectedInstallationPointIds([]);
    } else {
      setInstallationPoints([]);
    }
  }, [selectedMachineryId]);

  const fetchLocations = async () => {
    setLoadingLocations(true);
    setOptionsError('');
    try {
      const res = await axios.get(`${API}/locations`);
      setLocations([...res.data].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      setOptionsError('Locations could not be loaded. Retry loading the equipment list.');
      toast.error('Failed to load locations');
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchMachinery = async (locationId) => {
    setLoadingData(true);
    setOptionsError('');
    try {
      // Also fetch unassigned if locationId is 'unassigned'
      let res;
      if (locationId === 'unassigned') {
        res = await axios.get(`${API}/machinery/unassigned`);
      } else {
        res = await axios.get(`${API}/locations/${locationId}/machinery`);
      }
      setMachinery([...res.data].sort((a, b) => a.component.localeCompare(b.component)));
    } catch (error) {
      setOptionsError('Equipment could not be loaded. Retry loading the equipment list.');
      toast.error('Failed to load equipment');
    } finally {
      setLoadingData(false);
    }
  };

  const fetchInstallationPoints = async (machineryId) => {
    setLoadingData(true);
    try {
      const res = await axios.get(`${API}/machinery/${machineryId}/installation-points`);
      setInstallationPoints(res.data);
    } catch (error) {
      setInstallationPoints([]);
    } finally {
      setLoadingData(false);
    }
  };

  const resetForm = () => {
    setSerializedParts([]);
    setRequestId(crypto.randomUUID());
    setFormData({
      work_description: '',
      resolution: '',
      hours_spent: '',
      work_date: '',
      follow_up_required: '',
      notes: ''
    });
    setSelectedParts([]);
    setFollowUp(emptyFollowUp());
    setImageFiles([]);
    setImagePreviews([]);
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles(files);

    const previews = files.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
    });

    Promise.all(previews).then(setImagePreviews);
  };

  const handleProceedToForm = () => {
    if (!selectedMachineryId) {
      toast.error('Please select equipment');
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const imageUrls = [];
      for (const file of imageFiles) {
        const formDataImage = new FormData();
        formDataImage.append('file', file);
        const uploadRes = await axios.post(`${API}/upload-image`, formDataImage);
        imageUrls.push(uploadRes.data.image_url);
      }

      let workDateISO = null;
      if (formData.work_date) {
        workDateISO = new Date(formData.work_date).toISOString();
      }

      const partsUsedString = selectedParts
        .map((p) => (p.qty > 1 ? `${p.part_number} x${p.qty}` : p.part_number))
        .join(', ');

      const confirmedDeductions = selectedParts
        .filter((p) => !p.isNew)
        .map((p) => ({ part_number: p.part_number, qty: p.qty }));

      const newInventoryItems = selectedParts
        .filter((p) => p.isNew)
        .map((p) => ({ part_number: p.part_number, name: p.name }));

      const submitData = {
        request_id: requestId,
        serialized_actions: serializedPayload(serializedParts),
        machinery_id: selectedMachineryId,
        installation_point_ids: selectedInstallationPointIds,
        work_description: formData.work_description,
        resolution: formData.resolution || null,
        hours_spent: parseFloat(formData.hours_spent),
        parts_used: partsUsedString,
        work_date: workDateISO,
        follow_up_required: formData.follow_up_required || null,
        notes: formData.notes || null,
        images: imageUrls,
        confirmed_deductions: confirmedDeductions,
        new_inventory_items: newInventoryItems,
      };

      if (followUp.enabled && followUp.description.trim()) {
        submitData.follow_up = {
          description: followUp.description.trim(),
          priority: followUp.priority || null,
          due_date: followUp.due_date
            ? new Date(followUp.due_date + 'T23:59:59').toISOString()
            : null,
          kind: followUp.kind || 'minor',
        };
      }

      if (resolvesFollowUp?.id) {
        submitData.resolves_follow_up_id = resolvesFollowUp.id;
      }

      await axios.post(`${API}/maintenance-logs`, submitData);
      const totalUnits = confirmedDeductions.reduce((s, d) => s + (d.qty || 0), 0);
      const newCount = newInventoryItems.length;
      const parts = ['Maintenance log added'];
      if (totalUnits) parts.push(`${totalUnits} unit${totalUnits === 1 ? '' : 's'} deducted`);
      if (newCount) parts.push(`${newCount} new part${newCount === 1 ? '' : 's'} added (qty 0)`);
      if (resolvesFollowUp?.id) parts.push('follow-up resolved');
      toast.success(parts.join(' · '));
      window.dispatchEvent(new Event('maintenance-records-changed'));
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add maintenance log');
    } finally {
      setUploading(false);
    }
  };

  const getSelectedEquipmentName = () => {
    const machine = machinery.find(m => m.id === selectedMachineryId);
    const ips = installationPoints.filter((p) => selectedInstallationPointIds.includes(p.id));
    if (ips.length && machine) {
      return `${machine.component} @ ${ips.map((p) => p.label).join(', ')}`;
    }
    return machine?.component || '';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quick-maintenance-dialog">
        <DialogHeader>
          <DialogTitle>
            {step === 1 ? 'Quick Add Maintenance Log' : 'Add Maintenance Log'}
          </DialogTitle>
          <DialogDescription>
            {step === 1 
              ? 'Select the equipment to log maintenance for' 
              : `Recording maintenance for: ${getSelectedEquipmentName()}`}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-4">
            {optionsError && <div role="alert" className="text-sm text-red-700" data-testid="quick-options-error">
              {optionsError}
              <Button type="button" variant="link" data-testid="quick-options-retry"
                onClick={() => selectedLocationId ? fetchMachinery(selectedLocationId) : fetchLocations()}>Retry</Button>
            </div>}
            {/* Location Selection */}
            <div className="space-y-2">
              <Label htmlFor="quick-location">Plant Location *</Label>
              <select
                id="quick-location"
                data-testid="quick-location-select"
                value={selectedLocationId}
                disabled={loadingLocations}
                onChange={(e) => setSelectedLocationId(e.target.value)}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="">{loadingLocations ? 'Loading locations…' : 'Select location...'}</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
                <option value="unassigned">Unassigned Equipment</option>
              </select>
            </div>

            {/* Equipment Selection */}
            <div className="space-y-2">
              <Label htmlFor="quick-equipment">Equipment *</Label>
              <select
                id="quick-equipment"
                data-testid="quick-equipment-select"
                value={selectedMachineryId}
                onChange={(e) => setSelectedMachineryId(e.target.value)}
                disabled={!selectedLocationId || loadingData}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
              >
                <option value="">
                  {loadingData ? 'Loading...' : 'Select equipment...'}
                </option>
                {machinery.map((m) => (
                  <option key={m.id} value={m.id}>{m.component}</option>
                ))}
              </select>
            </div>

            {/* Installation Points (Optional multi-select) */}
            <div className="space-y-2">
              <Label>Installation Points (Optional)</Label>
              {selectedMachineryId && installationPoints.length > 0 ? (
                <InstallationPointMultiSelect
                  points={installationPoints}
                  value={selectedInstallationPointIds}
                  onChange={setSelectedInstallationPointIds}
                  testIdPrefix="quick-ip"
                  disabled={loadingData}
                />
              ) : (
                <p className="text-xs text-slate-500">
                  {selectedMachineryId
                    ? 'No installation points defined for this equipment yet — add them from the equipment page.'
                    : 'Pick equipment first to see its installation points.'}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleProceedToForm} 
                disabled={!selectedMachineryId || loadingData || loadingLocations || !!optionsError}
                data-testid="proceed-to-form-btn"
              >
                Continue →
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-slate-100 rounded-lg p-3 mb-4">
              <p className="text-sm text-slate-600">
                <strong>Equipment:</strong> {getSelectedEquipmentName()}
              </p>
              <Button 
                type="button" 
                variant="link" 
                className="p-0 h-auto text-sm"
                onClick={() => setStep(1)}
              >
                ← Change selection
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="quick-work-description">Work Description/Issue *</Label>
              <Textarea
                id="quick-work-description"
                data-testid="quick-work-description-input"
                value={formData.work_description}
                onChange={(e) => setFormData({ ...formData, work_description: e.target.value })}
                placeholder="Describe the issue or work performed"
                rows={3}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="quick-resolution">Resolution</Label>
              <Textarea
                id="quick-resolution"
                data-testid="quick-resolution-input"
                value={formData.resolution}
                onChange={(e) => setFormData({ ...formData, resolution: e.target.value })}
                placeholder="Describe how the issue was resolved"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quick-hours-spent">Hours Spent *</Label>
                <Input
                  id="quick-hours-spent"
                  data-testid="quick-hours-spent-input"
                  type="number"
                  step="0.1"
                  value={formData.hours_spent}
                  onChange={(e) => setFormData({ ...formData, hours_spent: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quick-work-date">Work Date & Time</Label>
                <Input
                  id="quick-work-date"
                  data-testid="quick-work-date-input"
                  type="datetime-local"
                  value={formData.work_date}
                  onChange={(e) => setFormData({ ...formData, work_date: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <WorkPartsFields
                parts={selectedParts}
                onPartsChange={setSelectedParts}
                serialized={serializedParts}
                onSerializedChange={setSerializedParts}
                machineryId={selectedMachineryId}
                disabled={uploading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="quick-follow-up">Follow-up notes (legacy text)</Label>
              <Textarea
                id="quick-follow-up"
                data-testid="quick-follow-up-textarea"
                value={formData.follow_up_required}
                onChange={(e) => setFormData({ ...formData, follow_up_required: e.target.value })}
                placeholder="Free-form notes (optional). Use the Follow-up Required section for tracked follow-ups."
                rows={2}
              />
            </div>

            {!resolvesFollowUp && (
              <FollowUpFields
                value={followUp}
                onChange={setFollowUp}
                testIdPrefix="quick-log-follow-up"
              />
            )}

            {resolvesFollowUp && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
                <div className="font-medium text-amber-900">
                  Resolving follow-up: {resolvesFollowUp.description}
                </div>
                <div className="text-xs text-amber-800 mt-1">
                  Submitting this log will mark that follow-up complete.
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="quick-notes">Additional Notes</Label>
              <Textarea
                id="quick-notes"
                data-testid="quick-notes-input"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional observations or issues found"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="quick-images">Issue/Work Images (Optional)</Label>
              <Input
                id="quick-images"
                data-testid="quick-images-input"
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
              />
              {imagePreviews.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {imagePreviews.map((preview, idx) => (
                    <img key={idx} src={preview} alt={`Preview ${idx + 1}`} className="h-24 rounded object-cover" />
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={uploading}>
                Cancel
              </Button>
              <Button type="submit" disabled={uploading} data-testid="quick-submit-log-btn">
                {uploading ? 'Adding...' : 'Add Maintenance Log'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuickMaintenanceDialog;
